/**
 * Hash generator — SHA digests via the Web Crypto API.
 *
 * `crypto.subtle.digest` is async, so `hashText` returns a Promise; the
 * component computes it in an effect (never during render → no hydration
 * mismatch). `bufToHex` is pure and unit-testable.
 */

export const HASH_ALGOS = ["SHA-1", "SHA-256", "SHA-384", "SHA-512"] as const;
export type HashAlgo = (typeof HASH_ALGOS)[number];

/** Lowercase hex string for a byte buffer. */
export function bufToHex(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let hex = "";
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, "0");
  }
  return hex;
}

/** Hex digest of `text` using the given SHA algorithm. */
export async function hashText(text: string, algo: HashAlgo): Promise<string> {
  const data = new TextEncoder().encode(text);
  const buf = await crypto.subtle.digest(algo, data);
  return bufToHex(buf);
}
